import { inject } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { AvailableCreditItem } from "@/types/accounts/AvailableCreditItem";
import { BankAccounts } from "acquaint-api/models/BankAccounts";
import { DatabaseFilterItem } from "@/types/system/DatabaseFilterItem";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { Products } from "acquaint-api/models/Products";
import { CreditNoteRequest } from "@/types/accounts/CreditNoteItem";
import { TaxRates } from "acquaint-api/models/TaxRates";
import { SetupFeeInfo } from "@/types/tenancies/SetupFeeInfo";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import { Transactions } from "acquaint-api/models/Transactions";
import { Statements } from "acquaint-api/models/Statements";
import { DefaultTenancyPropertyItem } from "@/types/accounts/DefaultTenancyPropertyItem";
import { BankAcccountLookupRequest } from "@/types/accounts/BankAccoutLookupRequest";
import {
  ExistingCredit,
  PaymentRequest,
  ReceivePaymentItem,
} from "@/types/accounts/ExistingCredit";
import { BankAccountInfo } from "@/types/tenancies/BankAccountInfo";
import {
  CreateInvoiceRequest,
  CreateReceiptRequest,
} from "@/types/accounts/CreateInvoiceRequest";
import {
  UnpaidChargeDto,
  UnpaidChargeRequest,
} from "@/types/accounts/UnpaidChargeRequest";
import { LinkedTransactionItem } from "@/types/accounts/LinkedTransactionItem";
import { TransactionsExtended } from "@/types/accounts/TransactionsExtended";
import { PaymentMethods } from "acquaint-api/models/PaymentMethods";
import { SplitTransactionDto } from "@/types/accounts/SplitTransactionDto";
import {
  RefundFromCompanyRequest,
  TransactionInfoItem,
} from "@/types/accounts/TransactionInfoItem";
import { AccountTotals } from "@/types/accounts/AccountTotals";
import {
  AccountTransactionItem,
  FetchAccountsRequest,
} from "@/types/accounts/AccountListItem";
import { PropertyBalanceItem } from "@/types/accounts/PropertyBalanceItem";
import { BankAccountCompany } from "@/types/accounts/BankAccountCompany";
import { TransferUnallocatedCreditsRequest } from "@/types/accounts/TransferUnallocatedCredits";
import {
  LastStatementRequest,
  StatementCheckItem,
  UndoLastStatementRequest,
} from "@/types/accounts/LastUndoItems";
import { TransferCompanyFunds } from "@/types/accounts/TransferCompanyFunds";
import {
  InvoiceReceiptTransaction,
  InvoiceReceiptTransactionRequest,
} from "@/types/accounts/InvoiceReceiptTransactions";

export function useAccountHelper() {
  const acquaintApiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const acquaintNextApiClient = inject<AcquaintApiNextClient>(
    "AcquaintApiNextClient",
  )!;

  return {
    lockTransaction: async (transactionId: number, lock: boolean) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `database/transactions/${transactionId}/lock`,
          {
            lock: lock,
            extraInfo: "",
          },
        );
        return true;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },

    clearIndividualTransaction: async (transactionId: number) => {
      try {
        await acquaintApiClient.httpGetVoid(
          `app/accounts/${transactionId}/ClearIndividualTransaction`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getAvailableCredits: async (
      contactId: number,
      propertyId: number,
      bankAccountId: number,
      isConsolidatedAccount: boolean,
    ) => {
      try {
        if (contactId <= 0) return;

        return await acquaintApiClient.httpPost<AvailableCreditItem>(
          `app/accounts/getavailablecredits`,
          {
            contactId,
            propertyId,
            bankAccountId,
            isConsolidatedAccount,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    readBankAccount: async (bankAccountType: number, companyId: number) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/GetBankAccountForCompany`,
          {
            companyId,
            bankAccountType,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getBankAccountById: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<BankAccounts>(
          `database/bankaccounts/${id}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchProductList: async (filters: DatabaseFilterItem[]) => {
      try {
        const request = {
          fields: ["id", "description"],
          filters,
        };
        return await acquaintApiClient.httpPost<IDDescriptionPair[]>(
          `database/products/list`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return [];
      }
    },

    getProductById: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<Products>(
          `database/products/${id}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getPaymentMethodById: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<PaymentMethods>(
          `database/paymentmethods/${id}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getTaxRateById: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<TaxRates>(
          `database/TaxRates/${id}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getFeesToTransferAmount: async (bankAccountId: number) => {
      try {
        return await acquaintApiClient.httpGet<number>(
          `app/accounts/${bankAccountId}/AmountFeesTransfer`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    readBankAccountCompany: async (bankAccountId: number) => {
      try {
        return await acquaintApiClient.httpGet<number>(
          `app/accounts/${bankAccountId}/ReadBankAccountCompany`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return 0;
      }
    },

    createCreditNote: async (request: CreditNoteRequest) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/CreateCreditNote`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchSetupFeeInfo: async (productId: number) => {
      try {
        if (productId <= 0) return undefined;

        return await acquaintNextApiClient.httpGet<SetupFeeInfo>(
          `/tenancies/${productId}/Product/SetupFee`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getPropertyCompanyId: async (propertyId: number) => {
      try {
        if (propertyId < 0) {
          return undefined;
        }
        return await acquaintApiClient.httpGet<number>(
          `app/accounts/${propertyId}/GetCompanyForProperty`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getDefaultTransaction: async () => {
      try {
        return await acquaintApiClient.httpGet<Transactions>(
          `database/transactions/defaultmodel`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getTransactionById: async (transactionId: number) => {
      try {
        return await acquaintApiClient.httpGet<Transactions>(
          `database/transactions/${transactionId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getTransactionInfoById: async (transactionId: number) => {
      try {
        return await acquaintApiClient.httpGet<TransactionsExtended>(
          `app/transactions/${transactionId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getLinkedTransactionsById: async (transactionId: number) => {
      try {
        return await acquaintNextApiClient.httpGet<LinkedTransactionItem[]>(
          `Accounts/transactions/${transactionId}/links`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getTenancyForProperty: async (contactId: number, propertyId: number) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/GetTenancyForProperty`,
          {
            contactId,
            propertyId,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getBankAccountForCompany: async (
      companyId: number,
      bankAccountType: number,
    ) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/GetBankAccountForCompany`,
          {
            companyId,
            bankAccountType,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getCompanyVATRegistered: async (companyId: number) => {
      try {
        if (companyId <= 0) return false;

        return await acquaintApiClient.httpGet<boolean>(
          `app/accounts/${companyId}/CompanyVATRegistered`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getDefaultTenancyProperty: async (contactId: number) => {
      try {
        if (contactId <= 0) return;

        return await acquaintApiClient.httpGet<DefaultTenancyPropertyItem>(
          `app/accounts/${contactId}/DefaultTenancyProperty`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getUnpaidCharges: async (request: UnpaidChargeRequest) => {
      try {
        return await acquaintNextApiClient.httpPost<UnpaidChargeDto[]>(
          `accounts/GetUnpaidCharges`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    payChargeWithExistingCredit: async (requestItem: ExistingCredit) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/PayChargeWithExistingCredit`,
          requestItem,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    receivePaymentForCharge: async (requestItem: PaymentRequest) => {
      try {
        return await acquaintApiClient.httpPost<ReceivePaymentItem>(
          `app/accounts/ReceivePaymentForCharge`,
          requestItem,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    createNewTransaction: async (newTransactionItem: Transactions) => {
      try {
        return await acquaintApiClient.httpPost<Transactions>(
          `database/transactions`,
          newTransactionItem,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    updateDbTransaction: async (id: number, transaction: Transactions) => {
      try {
        return await acquaintApiClient.httpPut<Transactions>(
          `database/transactions/${id}`,
          transaction,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    updateTransactions: async (
      transactionId: number,
      transaction: Transactions,
    ) => {
      try {
        await acquaintApiClient.httpPutVoid(
          `database/transactions/${transactionId}`,
          transaction,
        );
        await acquaintApiClient.httpPutVoid(
          `app/accounts/${transactionId}/UpdateTransactions`,
          transaction,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    setTransferFeesFlag: async (
      transactionId: number,
      transferFees: number,
    ) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/accounts/setTransferFeesFlag`,
          {
            transactionId,
            transferFees,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    createInvoice: async (request: CreateInvoiceRequest) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/CreateInvoice`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getInvoiceableTransactions: async (
      request: InvoiceReceiptTransactionRequest,
    ) => {
      try {
        return await acquaintApiClient.httpPost<InvoiceReceiptTransaction[]>(
          `app/accounts/invoices/invoiceabletransactions`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    createReceipt: async (request: CreateReceiptRequest) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/accounts/CreateReceipt`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getReceiptableTransactions: async (
      request: InvoiceReceiptTransactionRequest,
    ) => {
      try {
        return await acquaintApiClient.httpPost<InvoiceReceiptTransaction[]>(
          `app/accounts/receipts/availabletransactions`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    mergeSplitTransactions: async (splitId: number, total: number) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/accounts/MergeSplitTransactions`,
          {
            splitId,
            total,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    voidTransaction: async (transactionId: number) => {
      try {
        await acquaintApiClient.httpGetVoid(
          `app/accounts/${transactionId}/voidtransaction`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    setBankAccountLookup: async (request: BankAcccountLookupRequest) => {
      try {
        return await acquaintApiClient.httpPost<BankAccountInfo>(
          `app/bankaccounts/lookup`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchInvoicePDF: async (companyId: number, invoiceId: number) => {
      try {
        return await acquaintApiClient.getFile(
          `app/tenancy/${companyId}/company/${invoiceId}/invoicepdf`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchReceiptPDF: async (receiptId: number) => {
      try {
        return await acquaintApiClient.getFile(
          `app/tenancy/${receiptId}/receiptpdf`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    propertyRentGuarantee: async (propertyId: number) => {
      try {
        return await acquaintApiClient.httpGet<number>(
          `app/accounts/${propertyId}/PropertyRentGuarantee`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    downloadPDF: async (pdfTitle: string, pdf: Blob | undefined) => {
      if (!pdf) {
        return;
      }

      // Create url
      const url = window.URL.createObjectURL(pdf);

      // Link allows us to download the pdf
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", pdfTitle);
      document.body.appendChild(link);
      link.click();

      // Clears memory
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    fetchSplitTransactions: async (
      splitId: number,
      isCompanyAccount: boolean,
      bankAccountId: number,
    ) => {
      try {
        if (splitId <= 0) return;

        const request = {
          splitId,
          isCompanyAccount,
          bankAccountId,
        };

        return await acquaintNextApiClient.httpPost<SplitTransactionDto>(
          `accounts/SplitTransaction`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    checkUnallocatedTransactions: async (splitId: number) => {
      try {
        return await acquaintNextApiClient.httpGet<boolean>(
          `accounts/checkUnallocatedTransactions/${splitId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    isTransactionCleared: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<boolean>(
          `app/accounts/${id}/IsTransactionCleared`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    unlinkTransaction: async (unlinkId: number) => {
      try {
        await acquaintApiClient.httpGetVoid(
          `app/accounts/${unlinkId}/unlinktransaction`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    transactionunlinkchecker: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<TransactionInfoItem[]>(
          `app/accounts/${id}/transactionunlinkchecker`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    isBankAccountClient: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<boolean>(
          `app/bankaccounts/${id}/isBankAccountClient`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
        return false;
      }
    },

    readBankAccountType: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<number>(
          `app/bankaccounts/${id}/ReadBankAccountType`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
        return -1;
      }
    },

    refundFromCompany: async (request: RefundFromCompanyRequest) => {
      try {
        return await acquaintApiClient.httpPostVoid(
          `app/transactions/RefundFromCompany`,
          request,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    deleteTransaction: async (deleteId: number) => {
      try {
        await acquaintApiClient.httpDeleteVoid(
          `app/accounts/${deleteId}/deletetransaction`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    fetchDeleteBlockingTransactions: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<TransactionInfoItem[]>(
          `app/accounts/${id}/transactiondeletechecker`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
        return null;
      }
    },

    fetchAccountDefaultPropertyId: async (contactId: number) => {
      try {
        return await acquaintNextApiClient.httpGet<number>(
          `Accounts/${contactId}/DefaultProperty`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchAccounts: async (request: FetchAccountsRequest) => {
      try {
        if (request.contactId <= 0) return;

        return await acquaintNextApiClient.httpPost<AccountTransactionItem>(
          "Accounts/list",
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchAccountPropertiesBalances: async (
      contactId: number,
      bankAccountId: number,
      isCompanyAccount: boolean,
    ) => {
      try {
        const request = {
          contactId: contactId,
          bankAccountId: bankAccountId,
          isCompanyAccount: isCompanyAccount,
        };

        return await acquaintNextApiClient.httpPost<PropertyBalanceItem[]>(
          "Accounts/Properties/Balance",
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchContactAccountTotals: async (
      contactId: number,
      bankAccountId: number,
      propertyId: number,
    ) => {
      try {
        const request = {
          contactId,
          bankAccountId,
          propertyId,
        };
        return await acquaintNextApiClient.httpPost<AccountTotals>(
          "Accounts/ContactAccountTotals",
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchCompanies: async (): Promise<BankAccountCompany[]> => {
      try {
        return await acquaintNextApiClient.httpGet("accounts/bankaccounts");
      } catch (error) {
        displayErrorMessage(getErrorMessage(error));
        return [];
      }
    },

    transferUnallocatedCredits: async (
      request: TransferUnallocatedCreditsRequest,
    ) => {
      try {
        return await acquaintApiClient.httpPostVoid(
          "app/transactions/transferunallocatedcredits",
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    transferCompanyFunds: async (request: TransferCompanyFunds) => {
      try {
        return await acquaintApiClient.httpPostVoid(
          `app/accounts/transfercompanyfunds`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchStatements: async (
      contactid: number,
      propertyid: number,
      consolidatedaccount: boolean,
    ) => {
      try {
        return await acquaintApiClient.httpGet<IDDescriptionPair[]>(
          `/app/paymentruns/statements/list?contactid=${contactid}&propertyid=${propertyid}&consolidatedaccount=${consolidatedaccount}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return [];
      }
    },

    downloadStatementPDF: async (statementId: number, propertyName: string) => {
      try {
        const pdf = await acquaintApiClient.getFile(
          `app/paymentruns/${statementId}/FetchPaymentRunStatementPDF`,
        );
        if (!pdf) return;

        const url = window.URL.createObjectURL(pdf);

        // Link allows us to download the pdf
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          propertyName
            ? `Statement-${statementId}_Property-${propertyName}.pdf`
            : `Statement-${statementId}.pdf`,
        );
        document.body.appendChild(link);
        link.click();

        // Clears memory
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getLastStatementId: async (request: LastStatementRequest) => {
      try {
        return await acquaintApiClient.httpPost<number>(
          `app/paymentruns/getlaststatementid`,
          request,
        );
      } catch (error) {
        displayErrorMessage(getErrorMessage(error));
        return -1;
      }
    },

    checkStatementUndo: async (request: LastStatementRequest) => {
      try {
        return await acquaintApiClient.httpPost<StatementCheckItem[]>(
          `app/paymentruns/checkstatementundo`,
          request,
        );
      } catch (error) {
        displayErrorMessage(getErrorMessage(error));
        return [];
      }
    },

    undoLastStatement: async (request: UndoLastStatementRequest) => {
      try {
        return await acquaintApiClient.httpPostVoid(
          `app/paymentruns/undostatement`,
          request,
        );
      } catch (error) {
        displayErrorMessage(getErrorMessage(error));
      }
    },

    getPropertyFromFilename: (filename: string): string => {
      const match = filename.match(/- (.+)$/);
      if (!match) return "";
      return match[1];
    },

    getStatementById: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<Statements>(
          `database/Statements/${id}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },
  };
}
